import type { GraphData, ProcessMapping } from '../../../api/process-client/process-client.type';
import { ViewType } from '../../process/process.type';

interface ProcessMapPayload {
  graph_data: GraphData;
  mapping: ProcessMapping;
  currency_code: string;
  object_type?: 'project' | 'subproject' | 'work_period';
  use_groups?: boolean;
  /**
   * Source-cased workflow status names for this scope, display-only metadata
   * from the backend. Used to build the status-label resolver so customer
   * stage chips render in source casing. Optional so a backend that predates
   * this field (or a scope with no statuses) degrades to raw values.
   */
  statuses?: string[];
}

const VIEW_TYPE_LABELS: Record<ViewType, string> = {
  [ViewType.Count]: 'Count',
  [ViewType.Percentage]: 'Percentage',
  [ViewType.CycleTime]: 'Cycle Time',
  [ViewType.Cost]: 'Cost',
};

/**
 * Checks whether an artifact data object has the expected process-map shape.
 * Verifies `graph_data.nodes` is an array, `graph_data.edges` is a plain
 * object (the API encodes edges as a `Record<string, GraphEdge>`), and
 * `mapping.bf_to_customer` is a plain object. The mapping check is critical
 * because `getMappedStageNames` does `Object.keys(mapping.bf_to_customer)`,
 * which throws on `undefined` in production builds and tears down the
 * entire React tree.
 *
 * @param {Record<string, unknown>} data - Raw Flora artifact data payload.
 * @returns {boolean} True when graph data and mapping have the expected shape.
 */
function isProcessMapPayload(data: Record<string, unknown>): boolean {
  if (!data.graph_data || typeof data.graph_data !== 'object') return false;
  if (!data.mapping || typeof data.mapping !== 'object') return false;
  const graphData = data.graph_data as Partial<GraphData>;
  const mapping = data.mapping as Partial<ProcessMapping>;
  return (
    Array.isArray(graphData.nodes) &&
    graphData.edges != null &&
    typeof graphData.edges === 'object' &&
    !Array.isArray(graphData.edges) &&
    mapping.bf_to_customer != null &&
    typeof mapping.bf_to_customer === 'object' &&
    !Array.isArray(mapping.bf_to_customer)
  );
}

/**
 * Reads the initial process-map view-type from the export-route `initialState` seed.
 * Accepted values mirror the `ViewType` enum string values
 * (`'count' | 'percentage' | 'cycle_time' | 'cost'`). Falls back to `ViewType.Count`.
 *
 * @param {Record<string, unknown> | undefined} initialState - Optional state seed.
 * @returns {ViewType} The validated seed or `ViewType.Count`.
 */
function getInitialViewType(initialState: Record<string, unknown> | undefined): ViewType {
  const value = initialState?.viewType;
  if (typeof value === 'string') {
    for (const viewType of Object.values(ViewType)) {
      if (viewType === value) return viewType;
    }
  }
  return ViewType.Count;
}

/**
 * Reads the initial show-edges flag from the export-route `initialState` seed.
 *
 * @param {Record<string, unknown> | undefined} initialState - Optional state seed.
 * @returns {boolean} Defaults to `true` when no seed is provided.
 */
function getInitialShowEdges(initialState: Record<string, unknown> | undefined): boolean {
  const value = initialState?.showEdges;
  return typeof value === 'boolean' ? value : true;
}

export { VIEW_TYPE_LABELS, getInitialShowEdges, getInitialViewType, isProcessMapPayload };
export type { ProcessMapPayload };
